import { useEffect, useState } from 'react';
import { FocusTrap } from 'focus-trap-react';
import { Calendar, ArrowRight } from 'lucide-react';
import { FOCUS_TRAP_OPTIONS } from '@/constants/focus-trap';
import { useRescheduleTicketAcrossViews } from '@/api/plan-matriz-monthly';
import { cn } from '@/lib/utils';

interface RescheduleDialogProps {
  open: boolean;
  onClose: () => void;
  ticketId: number;
  correlativo: string;
  /** Current scheduled date as YYYY-MM-DD (or ISO date string). */
  currentDate: string | null | undefined;
  /** Optional callback invoked after a successful reschedule (e.g. to invalidate detail query). */
  onSuccess?: (newDate: string) => void;
}

const MONTHS_LONG = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function toDateInputValue(input: string | null | undefined): string {
  if (!input) return '';
  // Accept either YYYY-MM-DD or full ISO; trim the date portion.
  const m = input.slice(0, 10).match(DATE_RE);
  return m ? `${m[1] ?? ''}-${m[2] ?? ''}-${m[3] ?? ''}` : '';
}

function formatLong(dateStr: string): string {
  const m = dateStr.match(DATE_RE);
  if (!m) return dateStr;
  const y = m[1] ?? '';
  const month = Number(m[2]);
  const d = Number(m[3]);
  if (!month || month < 1 || month > 12) return dateStr;
  return `${d} de ${MONTHS_LONG[month - 1] ?? ''} ${y}`;
}

/**
 * Modal for rescheduling a ticket to an arbitrary date. Used both from the
 * ticket-detail page and from the calendar TicketCard quick action — same
 * UX, learned once. Backend-side rules (only PROGRAMADO/INICIADO can be
 * rescheduled) are still enforced by `useRescheduleTicketAcrossViews`;
 * callers are expected to gate visibility upstream.
 */
export function RescheduleDialog({
  open,
  onClose,
  ticketId,
  correlativo,
  currentDate,
  onSuccess,
}: RescheduleDialogProps) {
  const initialDate = toDateInputValue(currentDate);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [visible, setVisible] = useState(false);
  const reschedule = useRescheduleTicketAcrossViews();
  // Capture the stable reset reference. Pulling it out of the mutation object
  // keeps the effect deps narrow — depending on `reschedule` directly would
  // re-run the effect every time the mutation status changes (e.g. isPending
  // flips), wiping out the user's typed date mid-edit.
  const resetMutation = reschedule.reset;

  // Reset selected date + animate in when the dialog opens.
  useEffect(() => {
    if (open) {
      setSelectedDate(initialDate);
      requestAnimationFrame(() => { setVisible(true); });
    } else {
      setVisible(false);
    }
  }, [open, initialDate]);

  // Clear stale error state from any prior open of the dialog.
  useEffect(() => {
    if (open) resetMutation();
  }, [open, resetMutation]);

  if (!open) return null;

  const isUnchanged = selectedDate === '' || selectedDate === initialDate;
  const isPending = reschedule.isPending;
  const canConfirm = !isUnchanged && !isPending;

  const handleConfirm = () => {
    if (!canConfirm) return;
    reschedule.mutate(
      { ticketId, newDate: selectedDate },
      {
        onSuccess: () => {
          // Wrap the caller-provided onSuccess so a faulty callback can never
          // strand the dialog open after a successful reschedule.
          try {
            onSuccess?.(selectedDate);
          } catch (err) {
            console.error('[RescheduleDialog] onSuccess callback threw', err);
          }
          onClose();
        },
      },
    );
  };

  return (
    <FocusTrap focusTrapOptions={FOCUS_TRAP_OPTIONS}>
      <div
        className={cn(
          'fixed inset-0 z-sime-modal flex items-center justify-center p-4',
          'transition-opacity duration-150',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        onClick={() => { if (!isPending) onClose(); }}
      >
        <div className="absolute inset-0 bg-sime-overlay" />
        <div
          onClick={(e) => { e.stopPropagation(); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="reschedule-dialog-title"
          className={cn(
            'relative w-full max-w-[440px] rounded-2xl p-6 bg-sime-surface shadow-neu-xl',
            'transition-all duration-150',
            visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
          )}
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-xl bg-sime-accent/10">
              <Calendar className="h-5 w-5 text-sime-accent" />
            </div>
            <div className="min-w-0">
              <h3
                id="reschedule-dialog-title"
                className="text-[17px] font-semibold text-sime-text-primary"
              >
                Reprogramar {correlativo}
              </h3>
              <p className="text-sm text-sime-text-secondary mt-1">
                Selecciona una nueva fecha para este ticket. Solo aplica a tickets
                en estado Programado o Iniciado.
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-5">
            {initialDate ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-sime-text-tertiary">Fecha actual</span>
                <span className="font-medium text-sime-text-primary">
                  {formatLong(initialDate)}
                </span>
              </div>
            ) : null}

            <label className="block">
              <span className="block text-xs font-medium text-sime-text-secondary mb-1.5">
                Nueva fecha
              </span>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => { setSelectedDate(e.target.value); }}
                disabled={isPending}
                className={cn(
                  'w-full rounded-xl px-4 py-3 bg-transparent text-sm',
                  'shadow-neu-btn-active',
                  'focus:ring-2 focus:ring-sime-accent focus:outline-none',
                  'text-sime-text-primary disabled:opacity-50',
                )}
              />
            </label>

            {selectedDate && selectedDate !== initialDate ? (
              <div className="flex items-center gap-2 text-xs text-sime-text-tertiary bg-sime-surface-raised rounded-lg px-3 py-2">
                <span>{initialDate ? formatLong(initialDate) : '—'}</span>
                <ArrowRight className="h-3 w-3" />
                <span className="font-semibold text-sime-text-primary">
                  {formatLong(selectedDate)}
                </span>
              </div>
            ) : null}

            {reschedule.isError ? (
              <div role="alert" className="rounded-xl p-3 text-sm text-sime-danger bg-sime-danger/10 border border-sime-danger/20">
                Error al reprogramar. Verifica que el ticket esté en estado Programado o Iniciado.
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="px-4 py-2 rounded-xl text-sm font-medium text-sime-text-secondary hover:text-sime-text-primary transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canConfirm}
              className={cn(
                'px-4 py-2 rounded-xl text-sm font-medium text-white transition-opacity',
                canConfirm ? 'bg-sime-accent hover:opacity-90' : 'bg-sime-text-tertiary cursor-not-allowed opacity-60',
              )}
            >
              {isPending ? 'Reprogramando…' : 'Reprogramar'}
            </button>
          </div>
        </div>
      </div>
    </FocusTrap>
  );
}
